import { useEffect, useState } from "react";
import s from "../wizard.module.css";
import {
  Section,
  Field,
  TextField,
  SelectField,
  CedulaField,
  VehiculosField,
} from "../FieldHelpers";
import { useCedulaLookup } from "../../services/useCedulaLookup";
import { validateAutorizado } from "../../services/validationRules";
import { overlayClose } from "../../../../shared/utils/overlayClose";

const EMPTY = {
  id: null,
  cedula: "",
  nombre: "",
  apellidos: "",
  relacion: "Familiar",
  vehiculos: [],
};

export default function ModalAutorizado({
  open,
  onClose,
  formData,
  setFormData,
  selectedIndex,
}) {
  const [auth, setAuth] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const { buscar, resultado, estado, reset } = useCedulaLookup();

  useEffect(() => {
    if (selectedIndex !== null) {
      const found = formData.autorizados.find((a) => a.id === selectedIndex);
      setAuth(found ? { ...EMPTY, ...found } : EMPTY);
    } else {
      setAuth(EMPTY);
    }
    setErrors({});
    reset();
    // formData.autorizados y reset se omiten a propósito: reset se recrea en
    // cada render (no memoizado en useCedulaLookup) y agregarlo dispararía
    // este efecto (y el reseteo del formulario) en cada render, no solo al
    // abrir el modal o cambiar de selección.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedIndex]);

  useEffect(() => {
    if (resultado) {
      setAuth((prev) => ({
        ...prev,
        nombre: resultado.nombre || prev.nombre,
        apellidos: resultado.apellidos || prev.apellidos,
      }));
    }
  }, [resultado]);

  if (!open) return null;

  const handleChange = (e) => setAuth((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  function handleSave(e) {
    e.preventDefault();
    e.stopPropagation();
    const errs = validateAutorizado(auth);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setFormData((prev) => ({
      ...prev,
      autorizados: prev.autorizados.map((a) =>
        a.id === selectedIndex ? { ...auth, id: selectedIndex } : a,
      ),
    }));
    onClose();
  }

  return (
    <div className={`${s.wizard} ${s.overlay}`} {...overlayClose(onClose)}>
      <div className={s.modal} onClick={(e) => e.stopPropagation()}>
        <div className={s.modalAccent} />

        <div className={s.modalHeader}>
          <div>
            <h2 className={s.modalTitle}>Editar Autorizado</h2>
            <p className={s.modalSub}>
              Actualice los datos de la persona autorizada y las placas con las que entra a la
              filial.
            </p>
          </div>
          <button type="button" className={s.btnClose} onClick={onClose} title="Cerrar">
            <i className="fa-solid fa-xmark" />
          </button>
        </div>

        <div className={s.modalBody}>
          {Object.keys(errors).length > 0 && (
            <div className={s.alert}>
              <i className="fa-solid fa-triangle-exclamation" />
              Revisá los campos marcados antes de guardar.
            </div>
          )}

          <Section title="Datos del autorizado">
            <CedulaField
              value={auth.cedula}
              onChange={handleChange}
              onBlur={() => buscar(auth.cedula)}
              onBuscar={() => buscar(auth.cedula)}
              estado={estado}
              error={errors.cedula}
            />

            <div className={s.grid2}>
              <TextField
                name="nombre"
                label="Nombre"
                required
                placeholder="Nombre"
                value={auth.nombre}
                onChange={handleChange}
                error={errors.nombre}
              />
              <TextField
                name="apellidos"
                label="Apellidos"
                required
                placeholder="Apellidos"
                value={auth.apellidos}
                onChange={handleChange}
                error={errors.apellidos}
              />
            </div>
          </Section>

          <Section title="Relación y vehículos" subtitle="Con qué vínculo entra y con cuáles placas.">
            <div className={s.grid2b}>
              <SelectField
                name="relacion"
                label="Relación"
                required
                value={auth.relacion}
                onChange={handleChange}
              >
                <option value="Familiar">Familiar</option>
                <option value="Inquilino">Inquilino</option>
                <option value="Empleado">Empleado</option>
              </SelectField>

              <Field
                label="Vehículos"
                hint={
                  (auth.vehiculos || []).length ? (
                    <span className={s.labelHint}>{auth.vehiculos.length} asignada(s)</span>
                  ) : null
                }
              >
                <VehiculosField
                  variant="bandeja"
                  vehiculos={auth.vehiculos || []}
                  errors={errors.vehiculos}
                  onChange={(vehiculos) => setAuth((prev) => ({ ...prev, vehiculos }))}
                />
              </Field>
            </div>
          </Section>
        </div>

        <div className={s.modalFooter}>
          <button type="button" className={s.btnGhost} onClick={onClose}>
            Cancelar
          </button>
          <button type="button" className={s.btnPrimary} onClick={handleSave}>
            <i className="fa-solid fa-check" />
            <span className={s.labelLong}>Guardar Cambios</span>
            <span className={s.labelShort}>Guardar</span>
          </button>
        </div>
      </div>
    </div>
  );
}
